import { useState, useEffect, useCallback } from 'react'
import { vtApi } from '../api'
import { HiPlus, HiTrash, HiShieldCheck, HiCheckCircle, HiXCircle, HiClock } from 'react-icons/hi2'
import { Spinner } from '../hooks/useApi'
import { Badge, Switch, ConfirmModal, Field, Loader } from '../ui'

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--ui-border)', fontSize: 13,
  transition: 'border-color var(--ui-dur) var(--ui-ease)', background: 'var(--ui-surface)'
}

export default function VTSettingsCard() {
  const [cfg, setCfg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  
  const [newKey, setNewKey] = useState('')
  const [newKeyName, setNewKeyName] = useState('')
  const [keyError, setKeyError] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  // o'chirish tasdig'i kutilayotgan kalit
  const [pendingKey, setPendingKey] = useState(null)
  const [testingId, setTestingId] = useState(null)
  const [testResult, setTestResult] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setCfg(await vtApi.getConfig()) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const toggleEnabled = async (e) => {
    const val = e.target.checked
    setCfg(p => ({...p, enabled: val}))
    try {
      await vtApi.updateConfig({ enabled: val })
    } catch(err) {
      setError(err.message)
      setCfg(p => ({...p, enabled: !val}))
    }
  }

  const deleteKey = async (id) => {
    setPendingKey(null)
    const keys = cfg.keys.filter(k => k.id !== id)
    setCfg(p => ({...p, keys}))
    try {
      await vtApi.updateConfig({ keys })
    } catch (e) { setError(e.message); load() }
  }

  const handleAddKey = async () => {
    if (!newKey.trim()) return setKeyError('Kalitni kiriting')
    setKeyError(null)
    setSaving(true)
    const newK = { api_key: newKey.trim(), name: newKeyName.trim() || 'New Key' }
    const keys = [...(cfg.keys||[]), newK]
    try {
      const updated = await vtApi.updateConfig({ keys })
      setCfg(updated)
      setNewKey(''); setNewKeyName('')
      setShowAdd(false)
    } catch(e) { setError(e.message) }
    finally { setSaving(false) }
  }

  const handleTestKey = async (key) => {
    setTestingId(key.id)
    setTestResult(null)
    try {
      const r = await vtApi.testKey(key.api_key)
      if (r.ok) {
        setTestResult({ id: key.id, success: true, message: r.message })
      } else {
        setTestResult({ id: key.id, success: false, message: r.error })
      }
    } catch (e) {
      setTestResult({ id: key.id, success: false, message: e.message })
    } finally {
      setTestingId(null)
    }
  }

  if (loading) return <Loader state="working" block />
  if (!cfg) return null

  return (
    <div style={{ background: 'var(--ui-surface)', borderRadius: 8, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid var(--ui-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--ui-surface-2)', color: 'var(--ui-text-1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HiShieldCheck size={22} />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--ui-text-1)' }}>VirusTotal Integratsiyasi</h2>
            <div style={{ fontSize: 13, color: 'var(--ui-text-3)' }}>Fayllar va havolalarni xavfsizlikka tekshirish</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Switch
              checked={!!cfg.enabled}
              onChange={toggleEnabled}
              aria-label="VirusTotal integratsiyasini yoqish"
            />
          </div>
        </div>
      </div>

      {error && <div style={{ padding: '12px 24px', background: 'var(--ui-st-critical-bg)', color: 'var(--ui-st-critical)', fontSize: 13 }}>{error}</div>}

      <div style={{ padding: 24, opacity: cfg.enabled ? 1 : 0.5, pointerEvents: cfg.enabled ? 'auto' : 'none' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>API Kalitlar va Limiti ({cfg.keys?.length || 0})</div>
          <button className="btn btn-secondary" onClick={() => setShowAdd(!showAdd)}>
            <HiPlus/> Yangi kalit qo'shish
          </button>
        </div>

        {showAdd && (
          <div style={{ background: 'var(--ui-surface-2)', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px dashed var(--ui-border-2)' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
              <input style={{...inputStyle, flex: 1}} placeholder="Kalit nomi (masalan: Asosiy kalit)" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} />
              <Field error={keyError} style={{ flex: 2 }}>
                <input
                  style={{ ...inputStyle, ...(keyError ? { borderColor: 'var(--ui-st-critical)' } : null) }}
                  placeholder="VirusTotal API Key"
                  type="password"
                  aria-invalid={keyError ? true : undefined}
                  value={newKey}
                  onChange={e => { setNewKey(e.target.value); if (keyError) setKeyError(null) }}
                />
              </Field>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setKeyError(null) }}>Bekor qilish</button>
              <button className="btn btn-primary" onClick={handleAddKey} disabled={saving}>{saving ? <Spinner size={14}/> : 'Saqlash'}</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cfg.keys?.map(key => {
            const now = Math.floor(Date.now() / 1000);
            const minReset = Math.max(0, (key.reset_minute_at || 0) - now);
            const minLimit = key.minute_limit || 4;
            const dayLimit = key.day_limit || 500;
            const minPct = (key.used_minute / minLimit) * 100;
            const dayPct = (key.used_day / dayLimit) * 100;
            const isTested = testResult && testResult.id === key.id;

            return (
              <div key={key.id} style={{ border: '1px solid var(--ui-border)', borderRadius: 8, padding: 16, background: 'var(--ui-surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{key.name}</span>
                    {key.is_daily_exceeded ? (
                      <Badge tone="critical" icon={HiXCircle}>
                        Kunlik limit yetilgan ({key.used_day}/{dayLimit})
                      </Badge>
                    ) : key.is_rate_limited ? (
                      <Badge tone="medium" icon={HiClock}>
                        Daqiqalik limit ({key.used_minute}/{minLimit})
                      </Badge>
                    ) : (
                      <Badge tone="ok" icon={HiCheckCircle}>
                        Faol (Tayyor)
                      </Badge>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => handleTestKey(key)} disabled={testingId === key.id}>
                      {testingId === key.id ? <Spinner size={13} /> : 'Ulanishni tekshirish'}
                    </button>
                    <button
                      className="btn"
                      aria-label={`Kalitni o'chirish: ${key.name}`}
                      title="O'chirish"
                      style={{ padding: '4px 8px', color: 'var(--ui-st-critical)', background: 'var(--ui-st-critical-bg)' }}
                      onClick={() => setPendingKey(key)}
                    >
                      <HiTrash size={14}/>
                    </button>
                  </div>
                </div>

                {isTested && (
                  <div style={{
                    padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12,
                    background: testResult.success ? 'var(--ui-st-ok-bg)' : 'var(--ui-st-critical-bg)',
                    color: testResult.success ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)'
                  }}>
                    {testResult.success ? <HiCheckCircle size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> : <HiXCircle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />}
                    {testResult.message}
                  </div>
                )}

                <input type="text" style={{...inputStyle, color: 'var(--ui-text-2)', marginBottom: 16 }}
                  value={key.api_key || 'API kalit'} disabled />

                <div style={{ display: 'flex', gap: 24 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, color: 'var(--ui-text-3)' }}>
                      <span>Daqiqa limiti: {key.used_minute}/{minLimit}</span>
                      <span>{minReset > 0 && key.used_minute >= minLimit ? `${minReset}s qoldi` : 'Tayyor'}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--ui-surface-3)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: minPct >= 100 ? 'var(--ui-st-critical)' : 'var(--ui-text-1)', width: `${Math.min(100, minPct)}%`, transition: 'width var(--ui-dur) var(--ui-ease)' }}></div>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, color: 'var(--ui-text-3)' }}>
                      <span>Kunlik limit: {key.used_day}/{dayLimit}</span>
                      <span>{dayLimit - key.used_day} qoldi</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--ui-surface-3)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: dayPct >= 100 ? 'var(--ui-st-critical)' : 'var(--ui-text-1)', width: `${Math.min(100, dayPct)}%`, transition: 'width var(--ui-dur) var(--ui-ease)' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
          {(!cfg.keys || cfg.keys.length === 0) && !showAdd && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--ui-text-3)', fontSize: 13, background: 'var(--ui-surface-2)', borderRadius: 8 }}>
              Hozircha kalitlar yo'q
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!pendingKey}
        onClose={() => setPendingKey(null)}
        onConfirm={() => deleteKey(pendingKey.id)}
        title="API kalit o'chiriladi"
        text={pendingKey
          ? `«${pendingKey.name}» kaliti ro'yxatdan olib tashlanadi. VirusTotal so'rovlari qolgan kalitlar orqali ketadi.`
          : undefined}
        confirmLabel="O'chirish"
        danger
      />
    </div>
  )
}
